/**
 * Feature packs — the module-entitlement axis, shared by client and Cloud
 * Functions.
 *
 * A feature pack answers "which modules of the app does this tenant have at
 * all". It is deliberately coarse and orthogonal to the other tenant axes:
 * `tenant.tier` (which shell), `tenant.tradePackId` (what the work looks
 * like), and `tenant.featureFlags` (fine-grained toggles WITHIN granted
 * modules). See docs/FEATURE_PACKS_PLAN.md for the full model.
 *
 * The pack → feature map is a static registry, not Firestore data: packs
 * change with releases, not at runtime, and both packages need it without a
 * read (`functions/` gates service activation + MCP tools with the same map
 * the client gates nav/routes with).
 */

/** Every sellable pack. `standard` is listed for explicitness but is
 *  always granted — see resolveFeatures(). */
export const FEATURE_PACK_IDS = [
  'standard',
  'msp',
  'projects',
  'provisioning',
] as const;

export type FeaturePackId = (typeof FEATURE_PACK_IDS)[number];

/**
 * Stable feature keys. Gate sites reference these — never a pack ID — so a
 * feature can move between packs without touching call sites.
 */
export type FeatureKey =
  // standard
  | 'deals'
  | 'proposals'
  | 'quotes'
  | 'schedule'
  | 'jobs'
  | 'plate'
  | 'faults'
  | 'service-requests'
  | 'billing'
  | 'customers'
  | 'action-items'
  // msp
  | 'customer-services'
  | 'service-plans'
  | 'service-metrics'
  | 'starlink'
  | 'utilmate'
  | 'service-activation'
  // add-ons
  | 'projects'
  | 'provisioning';

export const FEATURE_PACK_FEATURES: Record<FeaturePackId, readonly FeatureKey[]> = {
  standard: [
    'deals',
    'proposals',
    'quotes',
    'schedule',
    'jobs',
    'plate',
    'faults',
    'service-requests',
    'billing',
    'customers',
    'action-items',
  ],
  msp: [
    'customer-services',
    'service-plans',
    'service-metrics',
    'starlink',
    'utilmate',
    'service-activation',
  ],
  projects: ['projects'],
  provisioning: ['provisioning'],
};

const ALL_FEATURES: readonly FeatureKey[] = FEATURE_PACK_IDS.flatMap(
  (packId) => FEATURE_PACK_FEATURES[packId],
);

/**
 * Resolve a tenant's `featurePacks` array to the set of granted features.
 *
 * - `undefined` / `null` (field never written) → ALL features. Mirrors how
 *   `tier` defaults to 'pro' and `tradePackId` to 'telco-installer': the
 *   existing prod tenant keeps working before the field is stamped.
 * - Any array (including `[]`) → union of the listed packs' features, PLUS
 *   `standard`, which is always granted — a tenant with no standard pack is
 *   a bricked tenant, not a product configuration.
 * - Unknown pack IDs are ignored (an old client reading a future pack ID
 *   must not throw).
 */
export function resolveFeatures(
  featurePacks: readonly string[] | null | undefined,
): Set<FeatureKey> {
  if (featurePacks == null) return new Set(ALL_FEATURES);
  const features = new Set<FeatureKey>(FEATURE_PACK_FEATURES.standard);
  for (const packId of featurePacks) {
    const packFeatures = FEATURE_PACK_FEATURES[packId as FeaturePackId];
    if (packFeatures) packFeatures.forEach((f) => features.add(f));
  }
  return features;
}

/** Does this `featurePacks` array grant the feature? Same fallback rules as
 *  resolveFeatures(). */
export function hasFeature(
  featurePacks: readonly string[] | null | undefined,
  feature: FeatureKey,
): boolean {
  return resolveFeatures(featurePacks).has(feature);
}

/** The add-on pack IDs a tenant can be sold beyond standard. */
export const ADDON_PACK_IDS: readonly FeaturePackId[] = FEATURE_PACK_IDS.filter(
  (id) => id !== 'standard',
);

/**
 * PLACEHOLDER list pricing, AUD cents per month, ex GST. Not wired to any
 * charging — the platform console shows these as suggestions next to the
 * pack toggles and the owner types the real number into the manual MRR
 * field. Reuben hasn't priced the packs yet (Aug 2026); these are
 * best-guess anchors against AU field-service SaaS (ServiceM8 / Tradify
 * territory), erring simple: one flat price per pack, no per-seat.
 * Replace with real pricing before any tenant is actually charged.
 */
export const PACK_LIST_PRICE_CENTS: Record<FeaturePackId, number> = {
  standard: 14900, // A$149/mo — the whole core platform
  msp: 14900, // A$149/mo — recurring-services machinery + billing integrations
  projects: 9900, // A$99/mo — full projects workspace
  provisioning: 4900, // A$49/mo — ordering pipeline for bigger teams
};

/** Suggested monthly total for a pack set (placeholder pricing). */
export function suggestedMonthlyCents(
  featurePacks: readonly string[] | null | undefined,
): number {
  const packs = featurePacks == null ? FEATURE_PACK_IDS : featurePacks;
  const unique = new Set<string>(['standard', ...packs]);
  let total = 0;
  for (const id of unique) {
    const price = PACK_LIST_PRICE_CENTS[id as FeaturePackId];
    if (price) total += price;
  }
  return total;
}
